import styled from "@emotion/styled";
import { motion } from "framer-motion";
import React from "react";
import { FiGithub } from "react-icons/fi";
import { useTheme } from "../context/ThemeContext";
import { darkTheme, lightTheme } from "../styles/theme";
import { RandomFloatingIcons, BACKEND_ICONS } from "./TechStackIcons";

const ProjectsSection = styled.section<{ theme: "light" | "dark" }>`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 70px 5% 40px;
  position: relative;
  overflow-x: hidden;
  overflow-y: auto;
  background: ${({ theme }) =>
    theme === "light" ? "#ffffff" : darkTheme.surface};

  /* Simplified pattern - subtle in dark mode */
  background-image: ${({ theme }) =>
    theme === "light"
      ? `
          repeating-linear-gradient(
            90deg,
            transparent,
            transparent 50px,
            rgba(0, 184, 255, 0.08) 50px,
            rgba(0, 184, 255, 0.08) 52px
          ),
          repeating-linear-gradient(
            0deg,
            transparent,
            transparent 50px,
            rgba(0, 184, 255, 0.08) 50px,
            rgba(0, 184, 255, 0.08) 52px
          )
        `
      : `
          repeating-linear-gradient(
            90deg,
            transparent,
            transparent 80px,
            rgba(255, 255, 255, 0.02) 80px,
            rgba(255, 255, 255, 0.02) 81px
          ),
          repeating-linear-gradient(
            0deg,
            transparent,
            transparent 80px,
            rgba(255, 255, 255, 0.02) 80px,
            rgba(255, 255, 255, 0.02) 81px
          )
        `};

  /* Simplified corner decorative - minimal in dark mode */
  &::before {
    content: "";
    position: absolute;
    top: 8%;
    left: 5%;
    width: 100px;
    height: 100px;
    background: ${({ theme }) =>
      theme === "light" ? `rgba(0, 184, 255, 0.06)` : `transparent`};
    border: 3px solid
      ${({ theme }) =>
        theme === "light"
          ? `rgba(0, 184, 255, 0.15)`
          : `rgba(255, 255, 255, 0.04)`};
    transform: rotate(-12deg);
    pointer-events: none;
    z-index: 0;
  }

  &::after {
    content: "";
    position: absolute;
    bottom: 12%;
    right: 6%;
    width: 90px;
    height: 90px;
    background: ${({ theme }) =>
      theme === "light" ? `rgba(0, 184, 255, 0.05)` : `transparent`};
    border: 3px solid
      ${({ theme }) =>
        theme === "light"
          ? `rgba(0, 184, 255, 0.12)`
          : `rgba(255, 255, 255, 0.03)`};
    transform: rotate(18deg);
    pointer-events: none;
    z-index: 0;
  }

  @media (min-width: 768px) {
    padding: 70px 10% 40px;
  }
`;

const ContentWrapper = styled(motion.div)`
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionHeader = styled.div`
  margin-bottom: 1.5rem;
`;

const SectionTitle = styled(motion.h2)<{ theme: "light" | "dark" }>`
  font-size: 1.5rem;
  color: ${({ theme }) =>
    theme === "light" ? lightTheme.text : darkTheme.text};
  margin-bottom: 0.8rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: -0.02em;
  display: inline-block;
  padding: 8px 16px;
  background: ${({ theme }) =>
    theme === "light" ? lightTheme.accent : darkTheme.accent};
  color: ${({ theme }) =>
    theme === "light" ? lightTheme.background : darkTheme.background};
  border: 4px solid
    ${({ theme }) => (theme === "light" ? lightTheme.border : darkTheme.border)};
  box-shadow: ${({ theme }) =>
    theme === "light" ? lightTheme.shadowLarge : darkTheme.shadowLarge};
  transform: rotate(-1deg);

  @media (min-width: 768px) {
    font-size: 1.8rem;
  }

  @media (min-width: 1024px) {
    font-size: 2rem;
  }
`;

const SectionDescription = styled.p<{ theme: "light" | "dark" }>`
  font-size: 0.85rem;
  color: ${({ theme }) =>
    theme === "light" ? lightTheme.textSecondary : darkTheme.textSecondary};
  max-width: 700px;
  line-height: 1.5;
  margin-top: 0.8rem;

  @media (min-width: 768px) {
    font-size: 0.9rem;
  }
`;

const ProjectGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 25px;
  }
`;

const ProjectCard = styled(motion.div)<{
  theme: "light" | "dark";
}>`
  background: ${({ theme }) =>
    theme === "light" ? lightTheme.background : darkTheme.surface};
  border: 4px solid
    ${({ theme }) => (theme === "light" ? lightTheme.border : darkTheme.border)};
  box-shadow: ${({ theme }) =>
    theme === "light" ? lightTheme.shadow : darkTheme.shadow};
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;

  &:hover {
    transform: translate(-4px, -4px);
    box-shadow: ${({ theme }) =>
      theme === "light"
        ? `12px 12px 0 ${lightTheme.border}`
        : `12px 12px 0 ${darkTheme.accent}`};
  }
`;

const ProjectHeader = styled.div<{
  theme: "light" | "dark";
}>`
  padding: 12px 16px;
  background: ${({ theme }) =>
    theme === "light" ? lightTheme.accent : darkTheme.accent};
  border-bottom: 4px solid
    ${({ theme }) => (theme === "light" ? lightTheme.border : darkTheme.border)};
`;

const ProjectTitle = styled.h3<{ theme: "light" | "dark" }>`
  font-size: 0.95rem;
  color: ${({ theme }) =>
    theme === "light" ? lightTheme.background : darkTheme.background};
  margin: 0;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  line-height: 1.3;

  @media (min-width: 768px) {
    font-size: 1.1rem;
  }
`;

const ProjectBody = styled.div`
  padding: 14px 16px;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ProjectDescription = styled.p<{ theme: "light" | "dark" }>`
  font-size: 0.8rem;
  color: ${({ theme }) =>
    theme === "light" ? lightTheme.textSecondary : darkTheme.textSecondary};
  line-height: 1.4;
  margin: 0;
  font-weight: 500;

  @media (min-width: 768px) {
    font-size: 0.85rem;
  }
`;

const ProjectTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const Tag = styled.span<{ theme: "light" | "dark" }>`
  font-size: 0.6rem;
  font-weight: 700;
  padding: 3px 8px;
  background: ${({ theme }) =>
    theme === "light" ? lightTheme.surface : darkTheme.background};
  border: 2px solid
    ${({ theme }) => (theme === "light" ? lightTheme.border : darkTheme.border)};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-family: "Space Mono", monospace;
  color: ${({ theme }) =>
    theme === "light" ? lightTheme.text : darkTheme.text};

  @media (min-width: 768px) {
    font-size: 0.65rem;
    padding: 4px 10px;
  }
`;

const ProjectActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: auto;
`;

const ActionButton = styled.a<{
  theme: "light" | "dark";
  variant: "primary" | "secondary";
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 6px 10px;
  flex: 1;
  background: ${({ variant, theme }) =>
    variant === "primary"
      ? theme === "light"
        ? lightTheme.accent
        : darkTheme.accent
      : theme === "light"
        ? lightTheme.background
        : darkTheme.surface};
  color: ${({ variant, theme }) =>
    variant === "primary"
      ? theme === "light"
        ? lightTheme.background
        : darkTheme.background
      : theme === "light"
        ? lightTheme.text
        : darkTheme.text};
  text-decoration: none;
  border: 3px solid
    ${({ theme }) => (theme === "light" ? lightTheme.border : darkTheme.border)};
  box-shadow: ${({ theme }) =>
    theme === "light" ? lightTheme.shadowSmall : darkTheme.shadowSmall};
  font-size: 0.7rem;
  font-weight: 900;
  transition: all 0.2s ease;
  text-transform: uppercase;
  letter-spacing: 0.05em;

  &:hover {
    transform: translate(-2px, -2px);
    box-shadow: ${({ theme }) =>
      theme === "light" ? lightTheme.shadow : darkTheme.shadow};
  }

  &:active {
    transform: translate(0, 0);
    box-shadow: ${({ theme }) =>
      theme === "light"
        ? "2px 2px 0 " + lightTheme.border
        : "2px 2px 0 " + darkTheme.border};
  }

  svg {
    font-size: 0.9rem;
  }

  @media (min-width: 768px) {
    padding: 8px 14px;
    font-size: 0.75rem;
  }
`;

const MoreProjectsCard = styled(motion.div)<{ theme: "light" | "dark" }>`
  grid-column: 1 / -1;
  background: ${({ theme }) =>
    theme === "light" ? lightTheme.accent : darkTheme.accent};
  border: 4px solid
    ${({ theme }) => (theme === "light" ? lightTheme.border : darkTheme.border)};
  box-shadow: ${({ theme }) =>
    theme === "light" ? lightTheme.shadowLarge : darkTheme.shadowLarge};
  padding: 20px 18px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 12px;

  &:hover {
    transform: translate(-4px, -4px);
    box-shadow: ${({ theme }) =>
      theme === "light"
        ? `12px 12px 0 ${lightTheme.border}`
        : `12px 12px 0 ${darkTheme.accent}`};
  }

  @media (min-width: 768px) {
    padding: 25px 30px;
  }
`;

const MoreProjectsTitle = styled.h3<{ theme: "light" | "dark" }>`
  font-size: 1.2rem;
  font-weight: 900;
  margin-bottom: 8px;
  color: ${({ theme }) =>
    theme === "light" ? lightTheme.background : darkTheme.background};
  text-transform: uppercase;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  @media (min-width: 768px) {
    font-size: 1.5rem;
  }
`;

const MoreProjectsText = styled.p<{ theme: "light" | "dark" }>`
  font-size: 0.8rem;
  font-weight: 700;
  color: ${({ theme }) =>
    theme === "light" ? lightTheme.background : darkTheme.background};
  margin: 0;

  @media (min-width: 768px) {
    font-size: 0.95rem;
  }
`;

const Projects: React.FC = () => {
  const { theme } = useTheme();

  const projects = [
    {
      title: "Rust Server API Template",
      description:
        "A versatile template for building RESTful interfaces with async runtime, middleware support, and production-ready architecture using Rust.",
      tags: ["Rust", "API", "Template"],
      link: "https://github.com/zk-steve/rust-web-api-microservice-template",
      linkLabel: "View Code",
      linkIcon: FiGithub,
    },
    {
      title: "Ethereum-Telegram Integration",
      description:
        "R&D of self-custodial EVM smart contract wallet integration in Telegram messenger. Supported by Ethereum Foundation grant.",
      tags: ["Ethereum", "Telegram", "Web3"],
      link: "https://github.com/ctrlsa/smart-contract-wallet-Ethereum-Account-Abstraction-Telegram",
      linkLabel: "View Code",
      linkIcon: FiGithub,
    },
  ];

  return (
    <ProjectsSection id="projects" theme={theme}>
      {/* Floating Tech Stack Icons */}
      <RandomFloatingIcons theme={theme} icons={BACKEND_ICONS} count={3} />

      <ContentWrapper
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        <SectionHeader>
          <SectionTitle theme={theme}>Featured Projects</SectionTitle>
          <SectionDescription theme={theme}>
            A showcase of my work in blockchain, AI, and full-stack development.
            Each project demonstrates technical excellence and innovation.
          </SectionDescription>
        </SectionHeader>

        <ProjectGrid>
          {projects.map((project, index) => {
            const ActionIcon = project.linkIcon;

            return (
            <ProjectCard
              key={project.title}
              theme={theme}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <ProjectHeader theme={theme}>
                <ProjectTitle theme={theme}>{project.title}</ProjectTitle>
              </ProjectHeader>
              <ProjectBody>
                <ProjectDescription theme={theme}>
                  {project.description}
                </ProjectDescription>
                <ProjectTags>
                  {project.tags.map((tag) => (
                    <Tag key={tag} theme={theme}>
                      {tag}
                    </Tag>
                  ))}
                </ProjectTags>
                <ProjectActions>
                  <ActionButton
                    theme={theme}
                    variant="primary"
                    href={project.link}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <ActionIcon />
                    {project.linkLabel}
                  </ActionButton>
                </ProjectActions>
              </ProjectBody>
            </ProjectCard>
            );
          })}

          <MoreProjectsCard
            theme={theme}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: projects.length * 0.1 }}
            onClick={() =>
              window.open("https://github.com/thuan2172001", "_blank")
            }
          >
            <MoreProjectsTitle theme={theme}>
              <FiGithub size={28} />
              More Projects
            </MoreProjectsTitle>
            <MoreProjectsText theme={theme}>
              Explore my GitHub for more amazing work!
            </MoreProjectsText>
          </MoreProjectsCard>
        </ProjectGrid>
      </ContentWrapper>
    </ProjectsSection>
  );
};

export default Projects;
